import React, {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useDatosExtra } from '../hooks/Otros';
import { useGetPets } from '../hooks/Pet/usePet';
import { DatosExtraGrouped } from '../types/Otros';
import { Pet } from '../types/Pet/types';
import { capitalizeFirstLetter } from '../utils/string';

type PetsContextType = {
  pets: Pet[] | undefined;
  isValidating: boolean;
  error: unknown;
  mutate: () => void;
  currentPet: Pet | null;
  setCurrentPet: React.Dispatch<React.SetStateAction<Pet | null>>;
  datosExtra: DatosExtraGrouped;
};

const PetsContext = createContext<PetsContextType | null>(null);

interface ProviderProps {
  children: ReactNode;
}

export const PetsContextProvider: React.FC<ProviderProps> = ({ children }) => {
  const { pets: rawPets, isValidating, error, mutate } = useGetPets();
  const { datosExtra } = useDatosExtra();

  const [currentPet, setCurrentPet] = useState<Pet | null>(null);

  // Normalizar nombres de mascotas (capitalizar primera letra)
  const pets = useMemo(() => {
    if (!rawPets) return undefined;
    return rawPets.map((pet) => ({
      ...pet,
      nombre: capitalizeFirstLetter(pet.nombre),
    }));
  }, [rawPets]);

  // Sincronizar currentPet
  useEffect(() => {
    // Caso 1: No hay pets (undefined o array vacío) → limpiar currentPet
    if (!pets || pets.length === 0) {
      setCurrentPet(null);
      return;
    }

    // Caso 2: Hay pets
    setCurrentPet((prev) => {
      // Si no hay mascota seleccionada, seleccionar la primera
      if (!prev) {
        return pets[0];
      }

      // Buscar si la mascota actual aún existe en la lista
      const updatedPet = pets.find((p) => p.id === prev.id);

      // Si existe, actualizar; si no, seleccionar la primera
      return updatedPet || pets[0];
    });
  }, [pets]);

  const contextValue = useMemo(
    () => ({
      pets,
      isValidating,
      error,
      mutate,
      currentPet,
      setCurrentPet,
      datosExtra,
    }),
    [pets, isValidating, error, mutate, currentPet, datosExtra],
  );

  return <PetsContext.Provider value={contextValue}>{children}</PetsContext.Provider>;
};

export const usePets = (): PetsContextType => {
  const context = useContext(PetsContext);

  if (!context) {
    throw new Error('usePets must be used within a PetsContextProvider');
  }

  return context;
};
